// Derived "blocked" state for tasks (DEC-0452 follow-up, Gal 20.09).
//
// Blocked is never stored. A task is blocked while at least one task that
// blocks it is not in the closed family, so the moment the blocker is done the
// dependent frees itself — there is no checkbox to forget to clear.
//
// The blocking arc lives in note_relations (source = the blocked task,
// target = the blocker, relation_type = BLOCKED_BY_RELATION). That table
// already carries the arcs between vb_records, with the generic relation UI
// helpers and a UNIQUE (source, target, type) that refuses a repeated arc; no
// new table is needed. (Measured 20.09: note_relations 204 rows and the one the
// entity queries read and write; vb_record_relations 2 rows. Corrects the
// first draft of this comment, which named vb_record_relations.)
//
// A dependency graph can be made circular by mistake (A blocks B blocks A),
// which would leave both blocked forever. `wouldCreateCycle` is the guard to
// run before writing an arc, and `blockedState` still terminates on a cycle
// that already exists and reports it, instead of looping.
//
// Pure module: no I/O.

import { isClosedStatus, isDroppedStatus, type StatusCatalog } from './taskStatus';

export const BLOCKED_BY_RELATION = 'blocked_by';

/** One arc: `taskId` cannot proceed until `blockerId` is closed. */
export interface BlockingArc {
  taskId: string;
  blockerId: string;
}

export interface TaskStatusRef {
  id: string;
  status: string | null | undefined;
}

export interface BlockedState {
  blocked: boolean;
  /** Blockers that are still open (not closed family), in arc order. */
  openBlockers: string[];
  /** Blockers that closed WITHOUT being completed (cancelled, not relevant,
   *  duplicate). They release the task, but the work it waited for may never
   *  arrive, so the screen shows a warning to check. */
  droppedBlockers: string[];
  /** True when this task can reach itself through blocking arcs. */
  inCycle: boolean;
}

/** A stored relation row, reduced to what the arc needs. Matches note_relations
 *  (source_id / target_id / relation_type). */
export interface BlockingRelationRow {
  id?: string;
  source_id: string;
  target_id: string;
  relation_type: string;
}

/** Keeps only the blocked_by rows and reads them as arcs. Every other relation
 *  type on the same table (part_of, party_to, ...) is ignored. */
export function arcsFromRelations(rows: BlockingRelationRow[]): BlockingArc[] {
  return rows
    .filter((r) => r.relation_type === BLOCKED_BY_RELATION)
    .map((r) => ({ taskId: r.source_id, blockerId: r.target_id }));
}

function blockersOf(arcs: BlockingArc[], taskId: string): string[] {
  return arcs.filter((a) => a.taskId === taskId).map((a) => a.blockerId);
}

/** True when `from` can reach `to` by following blocker links. */
function reaches(arcs: BlockingArc[], from: string, to: string): boolean {
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length) {
    const cur = stack.pop() as string;
    if (cur === to) return true;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const next of blockersOf(arcs, cur)) stack.push(next);
  }
  return false;
}

export function blockedState(
  catalog: StatusCatalog,
  tasks: TaskStatusRef[],
  arcs: BlockingArc[],
  taskId: string,
): BlockedState {
  const status = new Map(tasks.map((t) => [t.id, t.status] as const));
  // A task that is itself closed has nothing left to wait for, whatever its
  // blockers look like now.
  if (isClosedStatus(catalog, status.get(taskId))) {
    return { blocked: false, openBlockers: [], droppedBlockers: [], inCycle: false };
  }
  const openBlockers = blockersOf(arcs, taskId).filter((id) => {
    // A blocker we know nothing about (deleted, or not loaded) cannot be shown
    // to be closed, so it keeps blocking. Failing closed is the safe direction.
    if (!status.has(id)) return true;
    return !isClosedStatus(catalog, status.get(id));
  });
  const droppedBlockers = blockersOf(arcs, taskId).filter((id) => isDroppedStatus(catalog, status.get(id)));
  const inCycle = blockersOf(arcs, taskId).some((b) => reaches(arcs, b, taskId));
  return { blocked: openBlockers.length > 0, openBlockers, droppedBlockers, inCycle };
}

/** Would adding `taskId` blocked-by `blockerId` create a loop, or block a task
 *  by itself? Run before writing the arc. */
export function wouldCreateCycle(arcs: BlockingArc[], taskId: string, blockerId: string): boolean {
  if (taskId === blockerId) return true;
  return reaches(arcs, blockerId, taskId);
}

export type AddArcResult = { ok: true; arcs: BlockingArc[] } | { ok: false; error: string };

export function addBlockingArc(arcs: BlockingArc[], taskId: string, blockerId: string): AddArcResult {
  if (taskId === blockerId) return { ok: false, error: 'משימה לא יכולה לחסום את עצמה' };
  if (arcs.some((a) => a.taskId === taskId && a.blockerId === blockerId)) {
    return { ok: false, error: 'החסימה כבר קיימת' };
  }
  if (wouldCreateCycle(arcs, taskId, blockerId)) {
    return { ok: false, error: 'החסימה יוצרת מעגל: המשימה החוסמת תלויה בסופו של דבר במשימה הזאת' };
  }
  return { ok: true, arcs: [...arcs, { taskId, blockerId }] };
}
